/** @format */

import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { shouldSkipClientIpApiFetch } from '@utils/ipGeolocation';

let appInsights: ApplicationInsights | null = null;

/**
 * Initialize Application Insights with custom telemetry
 * This should be called once at application startup
 */
export const initializeAppInsights = (instrumentationKey: string): { appInsights: ApplicationInsights } | null => {
  if (!instrumentationKey) {
    console.warn('Application Insights: No instrumentation key provided. Telemetry will not be collected.');
    return null;
  }

  try {
    // Initialize Application Insights
    appInsights = new ApplicationInsights({
      config: {
        instrumentationKey,
        enableAutoRouteTracking: true, // Automatically track route changes
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        enableAjaxErrorStatusText: true,
        enableAjaxPerfTracking: true,
        maxAjaxCallsPerView: 500,
        disableFetchTracking: false,
        disableAjaxTracking: false,
        autoTrackPageVisitTime: true,
        // Avoid adding W3C trace / request-id headers to third-party fetches (e.g. ipapi.co), which breaks CORS preflight.
        enableCorsCorrelation: false,
        // Local/dev browsers can disallow unload handlers via Permissions-Policy.
        // App Insights can flush on pagehide/visibilitychange without this noisy listener.
        disablePageUnloadEvents: ['unload'],
        enableUnhandledPromiseRejectionTracking: true
      }
    });

    appInsights.loadAppInsights();

    // Add telemetry initializer to add custom properties to all telemetry
    appInsights.addTelemetryInitializer((envelope: any) => {
      envelope.tags = envelope.tags || [];

      // Add user information if available
      const userStr = localStorage.getItem('user');
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          envelope.tags['ai.user.id'] = user.user_id || user.email || 'anonymous';
          envelope.tags['ai.user.accountId'] = user.email || 'anonymous';

          // Add custom properties
          envelope.data = envelope.data || {};
          envelope.data.baseData = envelope.data.baseData || {};
          envelope.data.baseData.properties = envelope.data.baseData.properties || {};

          if (user.user_preferred_plan) {
            envelope.data.baseData.properties.userPlan = user.user_preferred_plan.plan_name;
          }
          if (user.email) {
            envelope.data.baseData.properties.userEmail = user.email;
          }
        } catch (error) {
          console.error('Error parsing user data for telemetry:', error);
        }
      }

      // Add application version
      envelope.data = envelope.data || {};
      envelope.data.baseData = envelope.data.baseData || {};
      envelope.data.baseData.properties = envelope.data.baseData.properties || {};
      envelope.data.baseData.properties.appVersion = process.env.APP_VERSION || '1.0.0';

      return true;
    });

    // Fetch and add IP and location information
    fetchLocationData();

    console.log('Application Insights initialized successfully');
    return { appInsights };
  } catch (error) {
    console.error('Failed to initialize Application Insights:', error);
    return null;
  }
};

/**
 * Fetch location data from IP geolocation service
 * This is done asynchronously and adds location context to subsequent telemetry
 */
const fetchLocationData = async () => {
  if (shouldSkipClientIpApiFetch()) {
    return;
  }
  try {
    // Using ipapi.co for free IP geolocation
    const response = await fetch('https://ipapi.co/json/');
    if (response.ok) {
      const data = await response.json();

      if (appInsights) {
        // Set location context
        appInsights.context.location.ip = data.ip;

        // Add location as custom properties for all future telemetry
        appInsights.addTelemetryInitializer((envelope: any) => {
          envelope.data = envelope.data || {};
          envelope.data.baseData = envelope.data.baseData || {};
          envelope.data.baseData.properties = envelope.data.baseData.properties || {};

          envelope.data.baseData.properties.ipAddress = data.ip;
          envelope.data.baseData.properties.city = data.city;
          envelope.data.baseData.properties.region = data.region;
          envelope.data.baseData.properties.country = data.country_name;
          envelope.data.baseData.properties.countryCode = data.country_code;
          envelope.data.baseData.properties.latitude = data.latitude;
          envelope.data.baseData.properties.longitude = data.longitude;
          envelope.data.baseData.properties.timezone = data.timezone;
          envelope.data.baseData.properties.org = data.org;

          return true;
        });

        console.log('Location data added to telemetry');
      }
    }
  } catch (error) {
    // Silently handle location fetch failures - this is expected in CI/test environments
    // where ipapi.co may be blocked by CORS or network policies
    // Location data is optional - telemetry will still work without it
  }
};

/**
 * Track a custom event with optional properties and measurements
 */
export const trackEvent = (name: string, properties?: { [key: string]: any }, measurements?: { [key: string]: number }) => {
  if (appInsights) {
    appInsights.trackEvent({ name, properties, measurements });
  }
};

/**
 * Track a page view manually (if needed beyond automatic tracking)
 */
export const trackPageView = (name?: string, uri?: string, properties?: { [key: string]: any }) => {
  if (appInsights) {
    appInsights.trackPageView({ name, uri, properties });
  }
};

/**
 * Track a user click event with element information
 */
export const trackClickEvent = (elementName: string, elementType: string, additionalProperties?: { [key: string]: any }) => {
  if (appInsights) {
    const properties = {
      elementName,
      elementType,
      timestamp: new Date().toISOString(),
      ...additionalProperties
    };

    appInsights.trackEvent({ name: 'UserClick' }, properties);
  }
};

/**
 * Track an exception/error
 */
export const trackException = (error: Error, severityLevel?: number) => {
  if (appInsights) {
    appInsights.trackException({ exception: error, severityLevel });
  }
};

/**
 * Track a metric
 */
export const trackMetric = (name: string, average: number, properties?: { [key: string]: any }) => {
  if (appInsights) {
    appInsights.trackMetric({ name, average }, properties);
  }
};

